import { useId } from 'react';

import {
  BREAKPOINTS,
  useMediaQuery,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import {
  BpkOverlayView,
  BpkPriceMarkerButton,
  MARKER_STATUSES,
} from '@skyscanner-internal/backpack-web/bpk-component-map';
import BpkPopover from '@skyscanner-internal/backpack-web/bpk-component-popover';
import { useI18n } from '@web-platform/shared-travel-tools-utils-frontend';

import { FlightCard } from '../../FlightCard/FlightCard';

import { LocationPinMarker } from './LocationPinMarker';

import type { MarkerProps } from '../ConfidenceMap';

import STYLES from './MapMarker.module.scss';

type Props = {
  marker: MarkerProps;
  selectedMarkerId?: string;
  onMarkerClick?: (id: string) => void;
  isOpen?: boolean;
  onClose?: () => void;
};

export const MapMarker = ({
  isOpen = false,
  marker,
  onClose,
  onMarkerClick,
  selectedMarkerId,
}: Props) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE);
  const { formatCurrency } = useI18n();
  const {
    cityName,
    coordinates,
    countryName,
    destinationAirportCode,
    exploreUrl,
    image,
    isDirect,
    price,
    priceLevel,
  } = marker;
  const isSelected = selectedMarkerId === destinationAirportCode;
  const position = { latitude: coordinates.latitude, longitude: coordinates.longitude };
  const priceLabel = formatCurrency(price.indicativePrice);
  const popoverId = useId();

  const showPriceButton = isSelected || priceLevel === 'PRICE_LEVEL_LOW';
  const isLow = priceLevel === 'PRICE_LEVEL_LOW';

  const trigger = showPriceButton ? (
    <span className={STYLES.markerTarget}>
      <BpkPriceMarkerButton
        label={priceLabel}
        className={isLow && !isSelected ? STYLES.priceMarkerLow : null}
        status={isSelected ? MARKER_STATUSES.selected : MARKER_STATUSES.unselected}
        onClick={() => onMarkerClick?.(destinationAirportCode)}
        buttonProps={{
          'aria-label': priceLabel,
          ...(!isMobile && {
            'aria-haspopup': 'dialog',
            'aria-expanded': String(isSelected && isOpen),
          }),
        }}
      />
    </span>
  ) : (
    <button
      type="button"
      className={STYLES.pinButton}
      aria-label={priceLabel}
      {...(!isMobile && {
        'aria-haspopup': 'dialog' as const,
        'aria-expanded': isSelected && isOpen,
      })}
      onClick={() => onMarkerClick?.(destinationAirportCode)}
    >
      <LocationPinMarker
        variant={priceLevel === 'PRICE_LEVEL_HIGH' ? 'danger' : 'primary'}
        aria-hidden
      />
    </button>
  );

  return (
    <BpkOverlayView position={position}>
      <div className={STYLES.markerWrapper}>
        {isMobile ? (
          trigger
        ) : (
          <BpkPopover
            id={`flight-card-popover-${popoverId}`}
            target={trigger}
            isOpen={isSelected && isOpen}
            onClose={onClose ?? (() => {})}
            label={cityName}
            placement="bottom"
            padded={false}
            className={STYLES.popover}
            aria-modal
          >
            <div className={STYLES.popoverCard}>
              <FlightCard
                destinationAirportCode={destinationAirportCode}
                cityName={cityName}
                countryName={countryName}
                image={image}
                price={price}
                priceLevel={priceLevel}
                isDirect={isDirect}
                exploreUrl={exploreUrl}
                area="map"
              />
            </div>
          </BpkPopover>
        )}
      </div>
    </BpkOverlayView>
  );
};
